import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

// Flash has not run in any browser since 2021. Ruffle, an open-source Flash
// emulator compiled to WebAssembly, plays the ActionScript 1/2 animations
// teachers still have, and much — not all — of ActionScript 3. Pinned to a
// version, and only fetched once a SWF is actually opened.
const RUFFLE_SRC = "https://cdn.jsdelivr.net/npm/@ruffle-rs/ruffle@0.6.0/ruffle.js";

let rufflePromise = null;

function loadRuffle() {
  if (window.RufflePlayer?.newest) return Promise.resolve(window.RufflePlayer);
  if (!rufflePromise) {
    window.RufflePlayer = window.RufflePlayer || {};
    window.RufflePlayer.config = {
      // Only play what we hand it; don't rewrite <object>/<embed> elsewhere.
      polyfills: false,
      autoplay: "auto",
      unmuteOverlay: "visible",
      letterbox: "on",
      showSwfDownload: false,
      logLevel: "error",
    };
    rufflePromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = RUFFLE_SRC;
      script.async = true;
      script.onload = () => resolve(window.RufflePlayer);
      script.onerror = () => {
        rufflePromise = null;
        reject(new Error("ruffle-unavailable"));
      };
      document.head.appendChild(script);
    });
  }
  return rufflePromise;
}

export default function SwfPlayer({ url }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let player = null;
    let cancelled = false;
    setStatus("loading");

    loadRuffle()
      .then((ruffle) => {
        if (cancelled || !containerRef.current) return undefined;
        player = ruffle.newest().createPlayer();
        player.style.width = "100%";
        player.style.height = "100%";
        containerRef.current.appendChild(player);
        // Newer releases namespace the API under ruffle(); older ones don't.
        const api = typeof player.ruffle === "function" ? player.ruffle() : player;
        return api.load({ url });
      })
      .then(() => {
        if (!cancelled) setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      if (player) player.remove();
    };
  }, [url]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "80vh",
        bgcolor: "#000",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

      {status === "loading" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            color: "#fff",
          }}
        >
          <CircularProgress sx={{ color: "#fff" }} />
          <Typography sx={{ fontSize: 14 }}>{t("Loading the animation...")}</Typography>
        </Box>
      )}

      {status === "error" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            textAlign: "center",
            color: "#fff",
          }}
        >
          <Typography>
            {t("This animation could not be played. Please check your connection and try again.")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
